import { ArrowBigUp, Command, CornerDownLeft, Keyboard } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { useLogsExplorer } from '@/components/shared/logs-explorer/hooks/use-logs-explorer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

type ShortcutKey = {
  label: ReactNode
}

type ShortcutItem = {
  keys: ShortcutKey[]
  description: string
}

const modKeyIcon: ShortcutKey = isMac
  ? { label: <Command className="size-3" /> }
  : { label: 'Ctrl' }

const shortcuts: ShortcutItem[] = [
  {
    keys: [modKeyIcon, { label: 'B' }],
    description: 'Toggle filter sidebar',
  },
  {
    keys: [modKeyIcon, { label: 'H' }],
    description: 'Toggle log volume chart',
  },
  {
    keys: [modKeyIcon, { label: <CornerDownLeft className="size-3" /> }],
    description: 'Run query',
  },
  {
    keys: [
      modKeyIcon,
      { label: <ArrowBigUp className="size-3" /> },
      { label: '?' },
    ],
    description: 'Show keyboard shortcuts',
  },
]

export const LogsKeyboardShortcuts = () => {
  const {
    isFilterSidebarCollapsed,
    setIsFilterSidebarCollapsed,
    isChartCollapsed,
    setIsChartCollapsed,
    runQuery,
    isShortcutsDialogOpen,
    setIsShortcutsDialogOpen,
  } = useLogsExplorer()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isModKey = isMac ? event.metaKey : event.ctrlKey

      if (isModKey && !event.shiftKey && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        event.stopImmediatePropagation()
        setIsFilterSidebarCollapsed(!isFilterSidebarCollapsed)
        return
      }

      if (isModKey && !event.shiftKey && event.key.toLowerCase() === 'h') {
        event.preventDefault()
        event.stopImmediatePropagation()
        setIsChartCollapsed(!isChartCollapsed)
        return
      }

      if (isModKey && event.key === 'Enter') {
        event.preventDefault()
        runQuery()
        return
      }

      if (
        isModKey &&
        event.shiftKey &&
        (event.key === '?' || event.key === '/')
      ) {
        event.preventDefault()
        setIsShortcutsDialogOpen(true)
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown, { capture: true })
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [
    isFilterSidebarCollapsed,
    setIsFilterSidebarCollapsed,
    isChartCollapsed,
    setIsChartCollapsed,
    runQuery,
    setIsShortcutsDialogOpen,
  ])

  useEffect(() => {
    if (!isShortcutsDialogOpen) {
      document.body.style.pointerEvents = ''
    }
    return () => {
      document.body.style.pointerEvents = ''
    }
  }, [isShortcutsDialogOpen])

  return (
    <Dialog
      open={isShortcutsDialogOpen}
      onOpenChange={setIsShortcutsDialogOpen}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="size-5" />
            Keyboard shortcuts
          </DialogTitle>
          <DialogDescription>
            Quick actions for navigating the logs explorer.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-2">
          {shortcuts.map((shortcut) => (
            <div
              key={shortcut.description}
              className="flex items-center justify-between"
            >
              <span className="text-sm">{shortcut.description}</span>
              <div className="flex items-center gap-1">
                {shortcut.keys.map((key, index) => (
                  <kbd
                    key={index}
                    className="flex h-6 min-w-6 items-center justify-center rounded border bg-muted px-1.5 font-mono text-xs"
                  >
                    {key.label}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
